import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { Button, Tooltip, Upload, message as antdMessage, Mentions, Avatar, Checkbox } from "antd";
import { Image, Paperclip, Smile, ThumbsUp, X, Send, Reply } from "lucide-react";
import EmojiPicker from "emoji-picker-react";
import type { UploadFile } from "antd";
import { useSendMessage } from "../../../hooks/mutations/useSendMessage";
import { useJoinAChatRoom } from "../../../hooks/queries/chat";
import { useUploadFileMutation } from "../../../hooks/mutations/use-upload-file";
import {
  formatMessageForServer,
  buildAlbumMetadata,
  buildImageMetadata,
  buildFileMetadata,
  buildVideoMetadata,
  formatMentionsForDisplayText,
} from "../../../utils/messageUtils";
import { generateVideoThumbnail, uploadThumbnail } from "../../../utils/videoUtils";
import { ReplyState, buildReplyMetadata } from "../../../utils/replyUtils";
import styles from "./MessageInput.module.scss";

const { Option } = Mentions;

interface MessageInputProps {
  roomId: string;
  replyTo?: ReplyState | null;
  onCancelReply?: () => void;
}

const MessageInput: React.FC<MessageInputProps> = ({ roomId, replyTo, onCancelReply }) => {
  const [message, setMessage] = useState("");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [uploadingImages, setUploadingImages] = useState<string[]>([]); // Track uploading UIDs
  const [requireConfirmation, setRequireConfirmation] = useState(false); // Track if message needs confirmation
  const mentionsRef = useRef<any>(null); // Using any because Mentions component doesn't properly expose focus method
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const inputAreaRef = useRef<HTMLDivElement>(null);

  // Fetch room info to get members and current user ID
  const { data: roomInfo } = useJoinAChatRoom({ roomId });
  const allMembers = useMemo(() => roomInfo?.Data?.Members || [], [roomInfo?.Data?.Members]);
  const currentUserId = roomInfo?.Data?.CUI || "";
  const roomType = roomInfo?.Data?.RoomType;
  const isChannelRoom = roomType === "C"; // Check if Channel room for confirmation feature

  // Send message mutation with currentUserId for optimistic update
  const { mutate: sendMessage, status } = useSendMessage(roomId, currentUserId);
  const isSending = status === "loading";
  const { uploadFilesAsync } = useUploadFileMutation();

  // Check if mobile device
  const isMobile = useMemo(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth <= 768;
    }
    return false;
  }, []);

  // Auto focus vào ô input khi component mount hoặc khi chuyển phòng chat
  // Chỉ auto-focus trên desktop, không focus trên mobile để tránh keyboard hiện ngay
  useEffect(() => {
    if (isMobile) {
      return; // Không auto-focus trên mobile
    }

    // Delay nhỏ để đảm bảo DOM đã render
    const timer = setTimeout(() => {
      mentionsRef.current?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, [roomId, isMobile]);

  // Safari Visibility Fix: Scroll input into view when focused
  useEffect(() => {
    if (!isMobile) return;

    const input = inputAreaRef.current;
    if (!input) return;

    const handleFocus = () => {
      // Force scroll after a short delay for keyboard to appear
      setTimeout(() => {
        input.scrollIntoView({ behavior: "smooth", block: "end" });
      }, 300);
    };

    input.addEventListener("focusin", handleFocus);
    return () => input.removeEventListener("focusin", handleFocus);
  }, [isMobile]);

  // Helper function để focus vào input với delay nhỏ
  const focusInput = useCallback(() => {
    // Delay nhỏ để đảm bảo state đã được cập nhật
    setTimeout(() => {
      mentionsRef.current?.focus();
    }, 50);
  }, []);

  // Prepare mention options
  // - Room type "U" (personal/direct chat) không có mention
  // - Lọc ra các members không có ContactName (abnr - avatar/name/basic info)
  // - Loại bỏ chính user hiện tại ra khỏi danh sách mention (so sánh CUI)
  // - Chỉ thêm @All cho các room type khác U
  const mentionOptions = useMemo(() => {
    // Room type U không có mention
    if (roomType === "U") {
      return [];
    }

    // Lọc members có đầy đủ thông tin (ContactName) và không phải là chính user hiện tại
    // So sánh với CUI vì currentUserId là Azure Communication User ID
    const validMembers = allMembers.filter(
      (member) => member.ContactName && member.ContactName.trim() !== "" && member.CUI !== currentUserId,
    );

    const MENTION_ALL = {
      Id: "0",
      ContactName: "All",
      AvatarUrl: "",
    };

    return [MENTION_ALL, ...validMembers];
  }, [allMembers, roomType, currentUserId]);

  // Helper function to add confirmation metadata if needed
  const addConfirmationMetadata = useCallback(
    (metadata: Record<string, string>) => {
      if (requireConfirmation && isChannelRoom) {
        return { ...metadata, needConfirmed: "true" };
      }
      return metadata;
    },
    [requireConfirmation, isChannelRoom],
  );

  const handleImageSelect = useCallback(
    async (file: File, fileList: File[]) => {
      // Upload component calls beforeUpload for EACH file
      // Only process when this is the first file to avoid duplicates
      if (fileList.length > 1 && fileList[0] !== file) {
        return false;
      }

      // Show loading message immediately
      const loadingKey = `sending-${Date.now()}`;
      console.log("Showing loading message:", loadingKey);
      antdMessage.loading({
        content: "Đang gửi tin nhắn...",
        key: loadingKey,
        duration: 0,
      });

      // Auto-send all images (single or multiple) without preview
      try {
        // Upload all images at once using new multi-file API
        const imageUrls = await uploadFilesAsync(fileList);

        // Build metadata based on count
        let metadata: Record<string, string>;
        if (fileList.length === 1) {
          metadata = buildImageMetadata(imageUrls[0], fileList[0]);
        } else {
          metadata = buildAlbumMetadata(imageUrls);
        }

        // Add confirmation metadata if checkbox is checked
        metadata = addConfirmationMetadata(metadata);

        // Build reply metadata if replying
        const replyMetadata = replyTo ? buildReplyMetadata(replyTo) : undefined;

        // Format message with mentions
        const formattedMessage = message.trim() ? formatMessageForServer(message.trim(), allMembers) : imageUrls[0];

        // Send message
        sendMessage(
          {
            content: formattedMessage,
            attachments: [],
            metadata: metadata,
            replyMetaData: replyMetadata,
          },
          {
            onSuccess: () => {
              antdMessage.destroy(loadingKey);
              setMessage("");
              setRequireConfirmation(false); // Reset checkbox after sending
              onCancelReply?.();
              focusInput();
            },
            onError: () => {
              antdMessage.destroy(loadingKey);
              antdMessage.error("Không thể gửi ảnh");
            },
          },
        );
      } catch (error) {
        antdMessage.destroy(loadingKey);
        antdMessage.error("Upload ảnh thất bại");
        console.error("Upload error:", error);
      }

      return false; // Prevent auto upload
    },
    [uploadFilesAsync, sendMessage, replyTo, onCancelReply, message, allMembers, focusInput, addConfirmationMetadata],
  );

  const removeImage = useCallback((uid: string) => {
    setFileList((prev) => prev.filter((file) => file.uid !== uid));
  }, []);

  // Handle file/video selection
  const handleFileSelect = useCallback(
    async (file: File) => {
      const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB for videos
      const isVideo = file.type.startsWith("video/");

      // Validate video size only
      if (isVideo && file.size > MAX_VIDEO_SIZE) {
        antdMessage.error("Video quá lớn! Giới hạn tối đa là 50MB");
        return false;
      }

      const loadingKey = `uploading-file-${Date.now()}`;
      antdMessage.loading({
        content: "Đang tải lên...",
        key: loadingKey,
        duration: 0,
      });

      try {
        // Upload file using new multi-file API
        const uploadedUrls = await uploadFilesAsync([file]);
        const uploadedUrl = uploadedUrls[0];

        let metadata: Record<string, string>;

        if (isVideo) {
          // Generate and upload thumbnail for video
          let thumbnailUrl = "";
          try {
            const thumbnailBlob = await generateVideoThumbnail(file);
            thumbnailUrl = await uploadThumbnail(thumbnailBlob);
          } catch (error) {
            console.warn("Thumbnail generation failed:", error);
          }

          metadata = buildVideoMetadata(uploadedUrl, file, thumbnailUrl);
        } else {
          metadata = buildFileMetadata(uploadedUrl, file);
        }

        // Add confirmation metadata if checkbox is checked
        metadata = addConfirmationMetadata(metadata);

        // Build reply metadata if replying
        const replyMetadata = replyTo ? buildReplyMetadata(replyTo) : undefined;

        // Send message
        sendMessage(
          {
            content: uploadedUrl,
            attachments: [],
            metadata: metadata,
            replyMetaData: replyMetadata,
          },
          {
            onSuccess: () => {
              antdMessage.destroy(loadingKey);
              antdMessage.success("Gửi file thành công!");
              setRequireConfirmation(false); // Reset checkbox after sending
              onCancelReply?.();
              focusInput();
            },
            onError: () => {
              antdMessage.destroy(loadingKey);
              antdMessage.error("Không thể gửi file");
            },
          },
        );
      } catch (error) {
        antdMessage.destroy(loadingKey);
        antdMessage.error("Upload file thất bại");
        console.error("Upload error:", error);
      }

      return false; // Prevent auto upload
    },
    [uploadFilesAsync, sendMessage, replyTo, onCancelReply, focusInput, addConfirmationMetadata],
  );

  // Handle paste images
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles: File[] = [];

    // Collect all image files from clipboard
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf("image") !== -1) {
        const file = item.getAsFile();
        if (file) {
          // Tạo tên file duy nhất để tránh trùng tên khi upload
          const timestamp = Date.now();
          const randomStr = Math.random().toString(36).substr(2, 6);
          const extension = file.type.split("/")[1] || "png";
          const uniqueName = `paste_${timestamp}_${i}_${randomStr}.${extension}`;

          // Tạo file mới với tên duy nhất
          const renamedFile = new File([file], uniqueName, { type: file.type });
          imageFiles.push(renamedFile);
        }
      }
    }

    if (imageFiles.length > 0) {
      e.preventDefault(); // Prevent default paste behavior

      // Always add to preview (both single and multiple)
      imageFiles.forEach((file, index) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target?.result) {
            const newFile: UploadFile = {
              uid: `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
              name: file.name,
              status: "done",
              url: e.target.result as string,
              originFileObj: file as any, // Type mismatch between File and RcFile
            };
            setFileList((prev) => [...prev, newFile]);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  }, []);

  const handleSend = useCallback(async () => {
    // If has images in preview, send as image or album based on count
    if (fileList.length > 0) {
      try {
        setUploadingImages(fileList.map((f) => f.uid));

        // Upload all images at once
        const filesToUpload = fileList.map((f) => f.originFileObj as File);
        const imageUrls = await uploadFilesAsync(filesToUpload);

        if (fileList.length === 1) {
          // Single image - send as type "image"
          const file = fileList[0].originFileObj as File;
          const imageUrl = imageUrls[0];

          // Build image metadata
          let imageMetadata = buildImageMetadata(imageUrl, file);

          // Add confirmation metadata if checkbox is checked
          imageMetadata = addConfirmationMetadata(imageMetadata);

          // Build reply metadata if replying
          const replyMetadata = replyTo ? buildReplyMetadata(replyTo) : undefined;

          // Format message with mentions
          const formattedMessage = message.trim() ? formatMessageForServer(message.trim(), allMembers) : imageUrl;

          // Send message with image
          sendMessage(
            {
              content: formattedMessage,
              attachments: [],
              metadata: imageMetadata,
              replyMetaData: replyMetadata,
            },
            {
              onSuccess: () => {
                setMessage("");
                setFileList([]);
                setUploadingImages([]);
                onCancelReply?.();
                focusInput();
              },
              onError: () => {
                antdMessage.error("Không thể gửi ảnh");
                setUploadingImages([]);
              },
            },
          );
        } else {
          // Multiple images - send as type "album"

          // Build album metadata
          let albumMetadata = buildAlbumMetadata(imageUrls);

          // Add confirmation metadata if checkbox is checked
          albumMetadata = addConfirmationMetadata(albumMetadata);

          // Build reply metadata if replying
          const replyMetadata = replyTo ? buildReplyMetadata(replyTo) : undefined;

          // Format message with mentions
          const formattedMessage = message.trim() ? formatMessageForServer(message.trim(), allMembers) : imageUrls[0];

          // Send album message
          sendMessage(
            {
              content: formattedMessage,
              attachments: [],
              metadata: albumMetadata,
              replyMetaData: replyMetadata,
            },
            {
              onSuccess: () => {
                setMessage("");
                setFileList([]);
                setUploadingImages([]);
                onCancelReply?.();
                focusInput();
              },
              onError: () => {
                antdMessage.error("Không thể gửi album");
                setUploadingImages([]);
              },
            },
          );
        }
      } catch (error) {
        antdMessage.error("Upload ảnh thất bại");
        setUploadingImages([]);
        console.error("Upload error:", error);
      }
    } else if (message.trim()) {
      // Text message only
      const formattedMessage = formatMessageForServer(message.trim(), allMembers);
      const replyMetadata = replyTo ? buildReplyMetadata(replyTo) : undefined;

      // Add confirmation metadata if checkbox is checked
      const metadata = requireConfirmation && isChannelRoom ? { needConfirmed: "true" } : undefined;

      sendMessage(
        {
          content: formattedMessage,
          attachments: [],
          metadata: metadata,
          replyMetaData: replyMetadata,
        },
        {
          onSuccess: () => {
            setMessage("");
            setRequireConfirmation(false); // Reset checkbox after sending
            onCancelReply?.();
            focusInput();
          },
          onError: (error) => {
            antdMessage.error("Không thể gửi tin nhắn");
            console.error("Send message error:", error);
          },
        },
      );
    }
  }, [
    message,
    fileList,
    sendMessage,
    allMembers,
    replyTo,
    onCancelReply,
    uploadFilesAsync,
    focusInput,
    addConfirmationMetadata,
    requireConfirmation,
    isChannelRoom,
  ]);

  // Handle emoji selection
  const handleEmojiClick = useCallback((emojiObject: { emoji: string }) => {
    const emoji = emojiObject.emoji;
    setMessage((prev) => prev + emoji);
    setShowEmojiPicker(false);
  }, []);

  // Send like (thumbs up)
  const handleSendLike = useCallback(() => {
    sendMessage(
      {
        content: "👍",
        attachments: [],
      },
      {
        onSuccess: () => {
          antdMessage.success("Đã gửi!");
        },
      },
    );
  }, [sendMessage]);

  // Determine if should show like button (when input is empty and no files)
  const showLikeButton = !message.trim() && fileList.length === 0;
  const isUploading = uploadingImages.length > 0;

  return (
    <div className={styles.messageInputContainer}>
      {/* Reply Preview */}
      {replyTo && (
        <div className={styles.replyPreviewContainer}>
          <div className={styles.replyBar} />
          <div className={styles.replyContent}>
            <div className={styles.replyHeader}>
              <Reply size={14} />
              <span className={styles.replyLabel}>Đang trả lời {replyTo.senderName}</span>
            </div>
            <div className={styles.replyMessage}>{formatMentionsForDisplayText(replyTo.content)}</div>
          </div>
          <Button
            type="text"
            size="small"
            icon={<X size={16} />}
            onClick={onCancelReply}
            className={styles.cancelReplyButton}
          />
        </div>
      )}
      {/* Image Preview Area */}
      {fileList.length > 0 && (
        <div className={styles.imagePreviewArea}>
          <div className={styles.previewList}>
            {fileList.map((file) => (
              <div key={file.uid} className={styles.previewItem}>
                <img src={file.url} alt={file.name} />
                {uploadingImages.includes(file.uid) && (
                  <div className={styles.uploadingOverlay}>
                    <span>Đang tải...</span>
                  </div>
                )}
                <Button
                  type="text"
                  size="small"
                  icon={<X size={14} />}
                  className={styles.removeButton}
                  onClick={() => removeImage(file.uid)}
                  disabled={uploadingImages.includes(file.uid) || isSending}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className={styles.inputArea} ref={inputAreaRef} onPaste={handlePaste}>
        {/* Toolbar */}
        <div className={styles.toolbar}>
          <Upload
            beforeUpload={(file, fileList) => {
              handleImageSelect(file, fileList);
              return false;
            }}
            showUploadList={false}
            accept="image/*"
            multiple
            disabled={isSending || uploadingImages.length > 0}
          >
            <Tooltip title="Đính kèm ảnh">
              <Button
                type="text"
                icon={<Image size={18} />}
                className={styles.toolButton}
                disabled={isSending || uploadingImages.length > 0}
              />
            </Tooltip>
          </Upload>

          <Upload
            beforeUpload={(file) => {
              handleFileSelect(file);
              return false;
            }}
            showUploadList={false}
            accept=".pdf,.docx,.xlsx,.txt,.zip,.mp4,.mov,.avi,.webm"
            disabled={isSending}
          >
            <Tooltip title="Đính kèm file/video">
              <Button type="text" icon={<Paperclip size={18} />} className={styles.toolButton} disabled={isSending} />
            </Tooltip>
          </Upload>

          {/* Confirmation Checkbox - Only for Channel rooms */}
          {isChannelRoom && (
            <Checkbox
              checked={requireConfirmation}
              onChange={(e) => setRequireConfirmation(e.target.checked)}
              disabled={isSending}
              className={styles.confirmationCheckbox}
            >
              Yêu cầu thành viên xác nhận đã đọc tin nhắn này
            </Checkbox>
          )}
        </div>

        {/* Input Field with Mentions */}
        <div className={styles.inputWrapper}>
          <Mentions
            ref={mentionsRef}
            placeholder="Nhập tin nhắn..."
            value={message}
            onChange={setMessage}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            className={styles.mentionsInput}
            autoSize={{ minRows: 1, maxRows: 3 }}
            disabled={isSending}
          >
            {mentionOptions.map((member) => (
              <Option key={member.Id} value={member.ContactName}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {member.Id === "0" ? (
                    <Avatar size={18} style={{ backgroundColor: "#87d068" }}>
                      A
                    </Avatar>
                  ) : (
                    <Avatar src={member.AvatarUrl} size={18}>
                      {member.ContactName?.charAt(0).toUpperCase()}
                    </Avatar>
                  )}
                  <span style={{ fontSize: "14px" }}>{member.ContactName}</span>
                </div>
              </Option>
            ))}
          </Mentions>

          {/* Emoji Picker */}
          <div className={styles.emojiPickerWrapper} ref={emojiPickerRef}>
            <Tooltip title="Emoji">
              <Button
                type="text"
                icon={<Smile size={20} />}
                className={styles.emojiButton}
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                disabled={isSending}
              />
            </Tooltip>
            {showEmojiPicker && (
              <div className={styles.emojiPickerContainer}>
                <EmojiPicker onEmojiClick={handleEmojiClick} width={320} height={400} />
              </div>
            )}
          </div>

          {/* Send or Like Button */}
          {showLikeButton ? (
            <Tooltip title="Gửi like">
              <Button
                type="text"
                icon={<ThumbsUp size={20} />}
                onClick={handleSendLike}
                loading={isSending}
                className={styles.sendButton}
                disabled={isSending}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Gửi">
              <Button
                type="text"
                icon={<Send size={20} />}
                onClick={handleSend}
                disabled={(!message.trim() && fileList.length === 0) || isUploading || isSending}
                loading={isSending || isUploading}
                className={styles.sendButton}
              />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(MessageInput);
